/** @file yawMarks: the yaw ring's zero tick at south and the arrow of the positive (clockwise) sense. */
import { Quaternion } from '@babylonjs/core/Maths/math.vector.js';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { axesLocal } from './axesLocal';
import type { GizmoFrame } from './gizmoFrame';
import { gizmoLine, type GizmoLine } from './gizmoLine';
import type { GizmoNodes } from './gizmoNodes';
import { GIZMO_PALETTE } from './gizmoPalette';
import { lineWidth } from './lineWidth';
import { ringTurn } from './ringTurn';

/** Tick from `TICK[0]` to `TICK[1]` x radius; the arrow on `ARC_R` from `ARC[0]` to `ARC[1]` rad clockwise of south. */
const TICK = [0.84, 1.16] as const;
const ARC_R = 1.1;
const ARC = [0.1, 0.5] as const;
const HEAD = { back: 0.08, side: 0.045 } as const;
const STEPS = 8;
/** South on the unit ring (ring parameter). */
const SOUTH = -Math.PI / 2;

/** The point at ring parameter `t`, radius `r` (unit frame X, Y), in Babylon local coordinates. */
const at = (t: number, r: number): Vec3 => axesLocal([r * Math.cos(t), r * Math.sin(t), 0]);

/** The arrow: an arc going clockwise seen from above (decreasing `t`) and a head at its end. */
function arrow(): { arc: Vec3[]; head: Vec3[] } {
  const arc = Array.from({ length: STEPS + 1 }, (_, i) => at(SOUTH - ARC[0] - ((ARC[1] - ARC[0]) * i) / STEPS, ARC_R));
  const t = SOUTH - ARC[1];
  const back = t + HEAD.back / ARC_R;
  return { arc, head: [at(back, ARC_R + HEAD.side), at(t, ARC_R), at(back, ARC_R - HEAD.side)] };
}

/** `update(frame)` with the rotate set; `lines` for the resize hand-off. */
export type YawMarks = { lines: readonly GizmoLine[]; setEnabled(on: boolean): void; update(f: GizmoFrame): void };

/**
 * Three lines under a pivot on the axes frame, built at south: the zero tick across the Z ring and the clockwise
 * arrow just past it. Each frame the pivot turns about Z so south lands on `f.yawZeroRad` (the parent's south in
 * Local space, `yawZero`); shown with the Z ring only. The `0° S` text is a label (`labelsOf`).
 */
export function yawMarks(scene: Scene, nodes: GizmoNodes): YawMarks {
  const pivot = new TransformNode('viewport-gizmo-yaw-marks', scene);
  pivot.rotationQuaternion = new Quaternion();
  pivot.parent = nodes.frame;
  const c = GIZMO_PALETTE.handle[2] ?? GIZMO_PALETTE.hover;
  const { arc, head } = arrow();
  const lines = [
    gizmoLine('viewport-gizmo-yaw-tick', [at(SOUTH, TICK[0]), at(SOUTH, TICK[1])], pivot, scene, c),
    gizmoLine('viewport-gizmo-yaw-arc', arc, pivot, scene, c),
    gizmoLine('viewport-gizmo-yaw-head', head, pivot, scene, c),
  ];
  return {
    lines,
    setEnabled: (on) => { for (const l of lines) l.mesh.setEnabled(on); },
    update: (f) => {
      const on = (f.show & 4) !== 0;
      for (const l of lines) l.mesh.setEnabled(on);
      if (!on) return;
      if (pivot.rotationQuaternion !== null) ringTurn(2, f.yawZeroRad - SOUTH, pivot.rotationQuaternion);
      for (const l of lines) l.style(c, lineWidth(f, 2));
    },
  };
}
